import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { FORM_OPTIONS, CONTACT } from '@/config/constants';
import { phoneSchema, formatPhoneNumber } from '@/lib/validation';

const contactFormSchema = z.object({
  name: z.string().trim().min(1, 'Full name is required').max(100, 'Name must be less than 100 characters'),
  phone: phoneSchema,
  email: z.string().trim().email('Invalid email address').max(255, 'Email must be less than 255 characters'),
  address: z.string().trim().min(1, 'Property address is required').max(200, 'Address must be less than 200 characters'),
  service: z.string().min(1, 'Please select a service'),
  timeline: z.string().min(1, 'Please select a timeline'),
  details: z.string().trim().max(1000, 'Details must be less than 1000 characters').optional(),
  website: z.string().max(0, 'Invalid submission').optional(),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

const selectClassName =
  'flex h-11 w-full rounded-lg border border-input bg-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-ring-smooth transition-all duration-300 hover:border-primary/50 cursor-pointer';

const inputClassName =
  'h-11 bg-white border-input focus-ring-smooth focus-visible:ring-primary transition-all duration-300 hover:border-primary/50 rounded-lg';

export default function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      address: '',
      service: '',
      timeline: '',
      details: '',
      website: '',
    },
  });

  const onSubmit = async (values: ContactFormValues) => {
    setIsSubmitting(true);

    // Check honeypot - if filled, silently reject
    if (values.website && values.website.length > 0) {
      logger.warn('ContactForm', 'Spam detected: honeypot field filled');
      setIsSubmitting(false);
      toast.error('There was an issue with your submission. Please try again.');
      return;
    }

    try {
      const formattedPhone = formatPhoneNumber(values.phone);
      
      const { data: insertedData, error: dbError } = await supabase.from('contacts').insert({
        name: values.name,
        email: values.email,
        phone: formattedPhone,
        address: values.address,
        service: values.service,
        timeline: values.timeline,
        message: values.details || null,
      }).select('id').single();

      if (dbError) {
        if (dbError.message?.includes('row-level security policy')) {
          toast.error(
            `You've already submitted a request recently. Please wait 24 hours or call us directly at ${CONTACT.PHONE}.`
          );
          setIsSubmitting(false);
          return;
        }
        throw dbError;
      }

      // Sync to JobNimbus (which also sends Teams notification)
      // Fire and forget - don't block user
      if (insertedData?.id) {
        supabase.functions.invoke('sync-contact-to-jobnimbus', {
          body: {
            id: insertedData.id,
            name: values.name,
            email: values.email,
            phone: formattedPhone,
            address: values.address,
            service: values.service,
            timeline: values.timeline,
            message: values.details || null,
          },
        }).catch((err) => {
          logger.error('ContactForm', 'JobNimbus sync error (non-blocking)', { error: err });
        });
      }

      toast.success("Thank you! We'll get back to you soon.");

      // Track Meta Pixel Lead event
      if (typeof window !== 'undefined' && (window as unknown as { fbq?: Function }).fbq) {
        (window as unknown as { fbq: Function }).fbq('track', 'Lead', {
          content_name: 'Contact Form Submission',
          content_category: values.service,
          value: values.timeline,
        });
      }

      form.reset();
    } catch (error) {
      logger.error('ContactForm', 'Error submitting contact form', { error });
      toast.error('Something went wrong. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">Full Name *</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" {...field} className={inputClassName} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">Phone Number *</FormLabel>
              <FormControl>
                <Input type="tel" placeholder={CONTACT.PHONE} {...field} className={inputClassName} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">Email Address *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className={inputClassName} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="address"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">Property Address *</FormLabel>
              <FormControl>
                <Input placeholder="123 Main St, Houston, TX 77001" {...field} className={inputClassName} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="service"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">What brings you to us today? *</FormLabel>
              <FormControl>
                <select {...field} className={selectClassName}>
                  <option value="">Select a service...</option>
                  {FORM_OPTIONS.services.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="timeline"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">When do you need this done?</FormLabel>
              <FormControl>
                <select {...field} className={selectClassName}>
                  <option value="">Select a timeline...</option>
                  {FORM_OPTIONS.timelines.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="details"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">Tell us more about your roof (optional)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Any additional details about your roofing needs..."
                  className="min-h-[100px] bg-white border-input focus-visible:ring-primary focus-ring-smooth transition-all duration-300 hover:border-primary/50 rounded-lg"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Honeypot field - hidden from users, catches bots */}
        <FormField
          control={form.control}
          name="website"
          render={({ field }) => (
            <FormItem className="absolute opacity-0 pointer-events-none h-0 overflow-hidden" aria-hidden="true">
              <FormLabel>Website</FormLabel>
              <FormControl>
                <Input {...field} tabIndex={-1} autoComplete="off" />
              </FormControl>
            </FormItem>
          )}
        />

        <Button
          type="submit"
          size="lg"
          disabled={isSubmitting}
          className="w-full bg-primary text-white hover:bg-primary/90 font-semibold h-12 rounded-lg text-base"
        >
          {isSubmitting ? 'Sending...' : 'Get My Free Estimate'}
        </Button>

        <p className="text-xs text-muted-foreground text-center mt-2">
          Your information is secure. We'll contact you the same day, typically within the same hour.
        </p>
      </form>
    </Form>
  );
}
